/**
 * @fileoverview Photo Uploader Component with S3 Integration
 * @module PhotoUploader
 */

import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Upload, X, Image, FileText, Loader2, Check } from 'lucide-react';
import { apiClient } from '../../api/client';

interface PhotoUploaderProps {
  findingId: string;
  onUploadComplete: (evidence: any) => void;
}

interface UploadingFile {
  file: File;
  progress: number;
  status: 'uploading' | 'confirming' | 'complete' | 'error';
  error?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/heif', 'application/pdf'];

const PhotoUploader: React.FC<PhotoUploaderProps> = ({ findingId, onUploadComplete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<Map<string, UploadingFile>>(new Map());

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload JPEG, PNG, HEIC, or PDF files.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File too large. Maximum size is 5MB.';
    }
    return null;
  };

  const uploadFile = async (file: File) => {
    const fileId = `${file.name}-${Date.now()}`;
    
    setUploadingFiles(prev => new Map(prev).set(fileId, {
      file,
      progress: 0,
      status: 'uploading'
    }));

    try {
      // Step 1: Get presigned URL
      const urlResponse = await apiClient.evidence.getUploadUrl({
        findingId,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });

      if (!urlResponse.data.success) {
        throw new Error(urlResponse.data.error?.message || 'Failed to get upload URL');
      }

      const { uploadUrl, s3Key } = urlResponse.data.data;

      // Step 2: Upload to S3
      setUploadingFiles(prev => {
        const updated = new Map(prev);
        const current = updated.get(fileId);
        if (current) {
          updated.set(fileId, { ...current, progress: 30 });
        }
        return updated;
      });

      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to storage');
      }

      setUploadingFiles(prev => {
        const updated = new Map(prev);
        const current = updated.get(fileId);
        if (current) {
          updated.set(fileId, { ...current, progress: 70, status: 'confirming' });
        }
        return updated;
      });

      // Step 3: Confirm upload
      const confirmResponse = await apiClient.evidence.confirmUpload({
        findingId,
        s3Key,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });

      if (!confirmResponse.data.success) {
        throw new Error(confirmResponse.data.error?.message || 'Failed to confirm upload');
      }

      setUploadingFiles(prev => {
        const updated = new Map(prev);
        const current = updated.get(fileId);
        if (current) {
          updated.set(fileId, { ...current, progress: 100, status: 'complete' });
        }
        return updated;
      });

      // Notify parent
      onUploadComplete(confirmResponse.data.data);

      // Remove from uploading list after a delay
      setTimeout(() => {
        setUploadingFiles(prev => {
          const updated = new Map(prev);
          updated.delete(fileId);
          return updated;
        });
      }, 2000);

    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadingFiles(prev => {
        const updated = new Map(prev);
        const current = updated.get(fileId);
        if (current) {
          updated.set(fileId, { 
            ...current, 
            status: 'error', 
            error: error.message || 'Upload failed' 
          });
        }
        return updated;
      });
    }
  };

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files) return;

    Array.from(files).forEach(file => {
      const error = validateFile(file);
      if (error) {
        toast.error(error);
        return;
      }
      uploadFile(file);
    });
  }, [findingId]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    e.target.value = ''; // Reset input
  }, [handleFiles]);

  const removeUpload = (fileId: string) => {
    setUploadingFiles(prev => {
      const updated = new Map(prev);
      updated.delete(fileId);
      return updated;
    });
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) {
      return <Image className="w-5 h-5" />;
    }
    return <FileText className="w-5 h-5" />;
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <label
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`
          relative block w-full p-8 border-2 border-dashed rounded-xl cursor-pointer
          transition-all duration-200
          ${dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
          }
        `}
      >
        <input
          type="file"
          multiple
          accept={ALLOWED_TYPES.join(',')}
          onChange={handleChange}
          className="sr-only"
        />
        <div className="flex flex-col items-center text-center">
          <Upload className={`w-10 h-10 mb-3 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
          <p className="text-gray-700 font-medium">
            {dragActive ? 'Drop files here' : 'Drag & drop photos or click to browse'}
          </p>
          <p className="text-gray-500 text-sm mt-1">
            JPEG, PNG, HEIC, or PDF • Max 5MB per file
          </p>
        </div>
      </label>

      {/* Upload Progress */}
      {uploadingFiles.size > 0 && (
        <div className="space-y-2">
          {Array.from(uploadingFiles.entries()).map(([fileId, upload]) => (
            <div
              key={fileId}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                upload.status === 'error' 
                  ? 'bg-red-50 border-red-200' 
                  : upload.status === 'complete'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className={`flex-shrink-0 ${
                upload.status === 'error' ? 'text-red-500' : 
                upload.status === 'complete' ? 'text-green-500' : 'text-gray-500'
              }`}>
                {getFileIcon(upload.file.type)}
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {upload.file.name}
                </p>
                {upload.status === 'error' ? (
                  <p className="text-xs text-red-600">{upload.error}</p>
                ) : upload.status === 'complete' ? (
                  <p className="text-xs text-green-600">Upload complete</p>
                ) : (
                  <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                )}
              </div>

              <div className="flex-shrink-0">
                {upload.status === 'uploading' || upload.status === 'confirming' ? (
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                ) : upload.status === 'complete' ? (
                  <Check className="w-5 h-5 text-green-500" />
                ) : (
                  <button
                    onClick={() => removeUpload(fileId)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoUploader;















